import { X } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { FormError } from '@/components/FormError'
import { Cast, ScrollStory, Typed, type StoryChapter } from '@/components/storybook/Chapters'
import { PickedUp } from '@/components/storybook/PickedUp'
import { circleSections } from '@/lib/sections'
import type { Me } from '@/lib/api'
import type { Family } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { counted, listed, localDay, monthDay, useOnThisDayStory, yearsAgo, type OnThisDayStoryYear } from '@/lib/home'
import { momentPhotoUrl } from '@/lib/moments'
import { useCurrentSpace } from '@/lib/spaces'
import { dayChapters, type DayChapter, type StoryPhoto } from '@/lib/storybook'
import { firstName } from '@/lib/tree'
import { WithFamily } from './FamilyPage'
import { ScreenLoader } from '@/components/Loader'

export const OnThisDayStoryPage = ({ me }: { me: Me }) => (
  <WithFamily>{(family) => <DayStory family={family} me={me} />}</WithFamily>
)

const DAY = /^\d{4}-\d{2}-\d{2}$/
type Moment = OnThisDayStoryYear['moments']['shown'][number]

// On this day's Storybook version: the same day in every earlier year, told oldest first, a year to a chapter, with
// that day's photos and moments. Like an album's storybook it says what it is and changes nothing; the day is the one
// Home showed (?day=), so a story opened just before midnight stays on that day.
function DayStory({ family, me }: { family: Family; me: Me }) {
  const [params] = useSearchParams()
  const [day] = useState(() => (DAY.test(params.get('day') ?? '') ? params.get('day')! : localDay()))
  const space = useCurrentSpace(family)
  const story = useOnThisDayStory(family.id, space?.id, day)
  const [picked, setPicked] = useState<StoryPhoto | null>(null)
  usePageFrame(family.id, { title: 'On this day', back: { to: `/circle/${family.id}`, label: 'Back to Home' } })
  const home = `/circle/${family.id}`

  if (story.isPending) return <ScreenLoader surface="viewer" />
  if (story.isError) return <FormError error={story.error} />
  const years = dayChapters(story.data, circleSections(me.sections, family))
  const photos = years.reduce((n, x) => n + x.photos.length + x.morePhotos, 0)
  const moments = years.reduce((n, x) => n + x.moments.length + x.moreMoments, 0)
  const cover = years.find((x) => x.photos.length)?.photos[0] ?? null
  const firstMomentPhoto = years.flatMap((x) => x.moments).find((x) => x.photos.length)

  return (
    <article
      aria-label={`On this day, ${monthDay(day)}, Storybook version`}
      className="fixed inset-0 z-50 overflow-hidden bg-viewer text-viewer-foreground"
    >
      {years.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-title text-balance">
            Nothing kept here happened on {monthDay(day)} in an earlier year yet.
          </p>
          <Link to={home} className="text-sm underline underline-offset-4">
            Back to Home
          </Link>
        </div>
      ) : (
        <ScrollStory
          testId="story-on-this-day"
          cover={cover}
          backdrop={firstMomentPhoto && <Backdrop moment={firstMomentPhoto} eager />}
          eyebrow="On this day"
          title={monthDay(day)}
          byline={`${listed(
            [photos > 0 && counted(photos, 'photo'), moments > 0 && counted(moments, 'moment')].filter(
              (x): x is string => !!x,
            ),
          )} from ${counted(years.length, 'year')}`}
          chapters={years.map((year) => chapterOf(year, day, family.id))}
          onPick={(photo) => setPicked(photo as StoryPhoto)}
          ending={
            <>
              <Cast people={peopleOf(years)} said={(n) => (n === 1 ? 'in 1 of these' : `in ${n} of these`)} />
              <p className="border-t border-viewer-foreground/15 pt-5 text-sm text-viewer-muted">
                Every photo and moment here was dated {monthDay(day)} by whoever added it, and is as they added it. Tap
                a photo to pick it up and see the original.
              </p>
              <Link to={home} className="self-start text-sm underline underline-offset-4">
                Back to Home
              </Link>
            </>
          }
        />
      )}

      <header className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center gap-2 px-3 pt-[calc(var(--safe-top)+0.5rem)] sm:px-5">
        <Link
          to={home}
          aria-label="Close the storybook and go back to Home"
          className="pointer-events-auto grid size-11 place-items-center rounded-full bg-viewer/55 backdrop-blur-md transition-colors outline-none hover:bg-viewer/80 focus-visible:ring-[3px] focus-visible:ring-ring/60"
        >
          <X aria-hidden />
        </Link>
        <p className="mx-auto rounded-full bg-viewer/60 px-3 py-1.5 text-xs font-semibold whitespace-nowrap backdrop-blur-md">
          Storybook version
        </p>
        <span className="size-11" aria-hidden />
      </header>

      <PickedUp photo={picked} from={picked ? `Added by ${picked.by}` : ''} onClose={() => setPicked(null)} />
    </article>
  )
}

// Everyone in the whole day, counted across every year.
function peopleOf(years: DayChapter[]) {
  const seen = new Map<string, DayChapter['people'][number]>()
  for (const p of years.flatMap((x) => x.people)) {
    const known = seen.get(p.id)
    seen.set(p.id, { ...p, photos: (known?.photos ?? 0) + p.photos })
  }
  return [...seen.values()].sort((a, b) => b.photos - a.photos || a.name.localeCompare(b.name))
}

function chapterOf(year: DayChapter, day: string, familyId: string): StoryChapter {
  const more = [
    year.morePhotos > 0 && (
      <Link key="photos" to={`/circle/${familyId}/photos?year=${year.year}`} className="underline underline-offset-4">
        {counted(year.morePhotos, 'more photo')}
      </Link>
    ),
    year.moreMoments > 0 && (
      <Link key="moments" to={`/circle/${familyId}/moments`} className="underline underline-offset-4">
        {counted(year.moreMoments, 'more moment')}
      </Link>
    ),
  ].filter(Boolean)
  const withPhoto = year.moments.find((x) => x.photos.length)
  return {
    key: String(year.year),
    heading: String(year.year),
    sub: yearsAgo(year.year, day),
    photos: year.photos,
    people: year.people,
    backdrop: withPhoto && <Backdrop moment={withPhoto} />,
    notes: year.moments.map((moment) => <MomentNote key={moment.id} moment={moment} />),
    coda: more.length > 0 && (
      <p className="text-sm text-viewer-muted">
        And{' '}
        {more.length === 2 ? (
          <>
            {more[0]} and {more[1]}
          </>
        ) : (
          more[0]
        )}{' '}
        from {year.year}.
      </p>
    ),
  }
}

function Backdrop({ moment, eager = false }: { moment: Moment; eager?: boolean }) {
  const photo = moment.photos[0]!
  return (
    <img
      src={momentPhotoUrl(photo.id, 'medium')}
      srcSet={`${momentPhotoUrl(photo.id, 'medium')} 1200w, ${momentPhotoUrl(photo.id, 'display')} 2000w`}
      sizes="112vw"
      width={photo.width}
      height={photo.height}
      alt=""
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
      className="sb-bg"
    />
  )
}

// A moment drifting by on paper, as it was written: its title, the start of its words, its first photo, and whose it
// is. Tapping it opens the moment itself.
function MomentNote({ moment }: { moment: Moment }) {
  const photo = moment.photos[0]
  return (
    <Link
      to={`/moment/${moment.id}`}
      aria-label={`Read ${moment.title}`}
      className="sb-note flex flex-col gap-2 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
    >
      {photo && (
        <img
          src={momentPhotoUrl(photo.id, 'medium')}
          width={photo.width}
          height={photo.height}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className="mb-1 max-h-72 w-full rounded-sm object-cover"
        />
      )}
      <span className="eyebrow text-sheet-muted">
        {moment.by.you ? 'Your moment' : `${firstName(moment.by.name)}’s moment`}
      </span>
      <Typed text={moment.title} className="text-xl leading-snug font-semibold text-balance" />
      {moment.body.trim() && (
        <span className="line-clamp-4 text-[0.9375rem] leading-relaxed text-pretty">{moment.body.trim()}</span>
      )}
      <span className="text-sm text-sheet-muted underline underline-offset-4">Read the moment</span>
    </Link>
  )
}
